import { useLocaleContext } from "#/components/context/user/LocaleContext"
import React, { useState } from "react"
import { useIntl } from "react-intl"
import { Box, Button, DownIcon, Menu, MenuItem, Stack, useTheme, GlobeIcon } from "@carrotfertility/carotene-core"
import { PreferredLocaleChangedDialog } from "./register/PreferredLocaleChangedDialog"
import { Locale, SupportedLocale } from "../../types/Locale"
import { useAllLocales } from "#/services/locale/hooks/useAllLocales"
import { useIntlSort } from "#/utils/hooks/useIntlSort"

interface UnauthPreferredLocaleProps {
  showModal?: boolean
  employeeId?: number
  // Only set in sign-in contexts. The registration flow passes preferredLocale directly in the createAccount API call, so sessionStorage is not needed there.
  persistLocaleForLogin?: boolean
}

function UnauthPreferredLocale({
  showModal,
  employeeId,
  persistLocaleForLogin
}: UnauthPreferredLocaleProps): React.ReactElement {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const { setLocale } = useLocaleContext()
  const intl = useIntl()
  const locale: SupportedLocale = Locale.getLocaleFromString(intl.locale)
  const [modalOpen, setModalOpen] = React.useState(false)
  const theme = useTheme()

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = (selectedLocaleString?: string) => {
    // this gets passed a SyntheticEvent if user clicks out of dropdown, don't change locale in that case
    if (selectedLocaleString && typeof selectedLocaleString === "string") {
      setLocale(Locale.getLocaleFromString(selectedLocaleString))
      if (persistLocaleForLogin) {
        sessionStorage.setItem("unauthPreferredLocale", selectedLocaleString)
      }
      if (showModal) {
        setModalOpen(true)
      }
    }
    setAnchorEl(null)
  }

  const allLocalesQuery = useAllLocales({ employeeId })
  const { localeComparator } = useIntlSort()

  const sortedLocaleOptions = React.useMemo(() => {
    if (!allLocalesQuery.data) {
      return []
    }
    const localeOptions = allLocalesQuery.data.map((localeString) => ({
      value: localeString,
      label: Locale.getDisplayNameFromString(localeString) ?? ""
    }))
    return localeOptions.toSorted((a, b) => localeComparator(a.label, b.label))
  }, [allLocalesQuery.data, localeComparator])

  return (
    <>
      {!allLocalesQuery.isLoading && (
        <Box>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            id="show-menu"
            aria-label={intl.formatMessage({ defaultMessage: "Open menu" })}
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup={true}
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
          >
            <Stack alignItems="center" direction="row" spacing={theme.tokens.spacing.xs}>
              <GlobeIcon fontSize="small" />
              {locale.displayName}
              <DownIcon />
            </Stack>
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "show-menu"
            }}
          >
            {sortedLocaleOptions.map((option) => (
              <MenuItem key={option.value} value={option.label} onClick={() => handleClose(option.value)}>
                {option.label}
              </MenuItem>
            ))}
          </Menu>
          {/* For contexts like Registration, we want to show a modal giving user more info about language selection */}
          {showModal && (
            <PreferredLocaleChangedDialog preferredLocale={locale} modalOpen={modalOpen} setModalOpen={setModalOpen} />
          )}
        </Box>
      )}
    </>
  )
}

export { UnauthPreferredLocale }
